/**
 * EAN-13 desenhado como barras — o que faz a etiqueta valer alguma coisa.
 *
 * O protótipo desenha barras decorativas de largura sorteada: bonito na tela,
 * ilegível no leitor. Uma etiqueta que o PDV não bipa é papel colado na peça.
 *
 * ## Por que aqui e não numa biblioteca
 *
 * A norma do EAN-13 são três tabelas de sete bits e uma tabela de paridade —
 * as quatro cabem nesta tela. Trazer um pacote para isso somaria uma
 * dependência de terceiro ao caminho que imprime a identidade de cada peça do
 * estoque, para substituir sessenta linhas que não mudam desde 1977.
 *
 * ## Por que a saída é uma cadeia de módulos, e não um `<svg>`
 *
 * O "módulo" é a unidade de largura da norma: o símbolo inteiro tem 95 deles,
 * `1` é barra e `0` é espaço. Devolver a cadeia deixa o desenho por conta de
 * quem chama (o SVG da tela, o teste que confere bit a bit) e mantém esta
 * função puramente aritmética — dá para testá-la contra a norma sem montar
 * DOM nenhum.
 *
 * O dígito verificador é o MESMO cálculo de `digitoVerificadorEAN13` no Go
 * (`api/internal/modulos/catalogo/grade.go`). Ele está nos dois lados de
 * propósito: lá para gerar, aqui para recusar desenhar um código adulterado —
 * um símbolo com dígito errado é impresso, colado na peça e só falha no caixa.
 */

/** Codificação do grupo esquerdo, paridade ímpar. */
const ESQUERDA_L = [
  '0001101', '0011001', '0010011', '0111101', '0100011',
  '0110001', '0101111', '0111011', '0110111', '0001011',
]

/** Codificação do grupo esquerdo, paridade par. */
const ESQUERDA_G = [
  '0100111', '0110011', '0011011', '0100001', '0011101',
  '0111001', '0000101', '0010001', '0001001', '0010111',
]

/** Codificação do grupo direito — o complemento de `ESQUERDA_L`. */
const DIREITA_R = [
  '1110010', '1100110', '1101100', '1000010', '1011100',
  '1001110', '1010000', '1000100', '1001000', '1110100',
]

/**
 * O primeiro dígito do EAN-13 não vira barra nenhuma: ele é codificado no
 * PADRÃO DE PARIDADE dos seis dígitos seguintes. É o truque que permitiu ao
 * EAN-13 caber no mesmo símbolo do UPC-A, de doze dígitos.
 */
const PARIDADE = [
  'LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG',
  'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL',
]

const GUARDA_LATERAL = '101'
const GUARDA_CENTRAL = '01010'

/** 3 + 42 + 5 + 42 + 3 — o comprimento que a norma fixa. */
export const MODULOS_POR_SIMBOLO = 95

/**
 * Pesos 1 e 3 alternados da esquerda para a direita, complemento da dezena.
 * Espelha `digitoVerificadorEAN13` do Go.
 */
export function digitoVerificadorEAN13(doze: string): number {
  let soma = 0
  for (let i = 0; i < doze.length; i++) {
    const valor = doze.charCodeAt(i) - 48
    soma += i % 2 === 1 ? valor * 3 : valor
  }
  return (10 - (soma % 10)) % 10
}

/** Treze dígitos e o verificador conferindo. Espelha `CodigoBarrasValido`. */
export function codigoBarrasValido(codigo: string): boolean {
  if (!/^\d{13}$/.test(codigo)) return false
  return codigo.charCodeAt(12) - 48 === digitoVerificadorEAN13(codigo.slice(0, 12))
}

/**
 * A cadeia de 95 módulos do símbolo — `1` é barra, `0` é espaço.
 *
 * Devolve `null` para código que não é EAN-13 válido, e quem chama desenha
 * outra coisa no lugar. Desenhar mesmo assim produziria um símbolo que o
 * leitor recusa, o que é pior do que não ter símbolo: a etiqueta parece
 * pronta e falha no balcão.
 */
export function modulosEAN13(codigo: string): string | null {
  if (!codigoBarrasValido(codigo)) return null

  // As buscas nas tabelas não podem falhar — `codigoBarrasValido` já garantiu
  // treze dígitos, e as quatro tabelas têm dez entradas. As checagens existem
  // porque o `noUncheckedIndexedAccess` do projeto não conhece essa garantia,
  // e um `!` aqui apagaria a única rede que sobra se alguém editar uma tabela
  // e deixar nove linhas.
  const paridade = PARIDADE[codigo.charCodeAt(0) - 48]
  if (paridade === undefined) return null

  let esquerda = ''
  for (let i = 0; i < 6; i++) {
    const digito = codigo.charCodeAt(1 + i) - 48
    const bits = paridade[i] === 'L' ? ESQUERDA_L[digito] : ESQUERDA_G[digito]
    if (bits === undefined) return null
    esquerda += bits
  }

  let direita = ''
  for (let i = 0; i < 6; i++) {
    const bits = DIREITA_R[codigo.charCodeAt(7 + i) - 48]
    if (bits === undefined) return null
    direita += bits
  }

  return GUARDA_LATERAL + esquerda + GUARDA_CENTRAL + direita + GUARDA_LATERAL
}

/**
 * As barras como retângulos, já agrupadas: módulos `1` vizinhos viram UM
 * retângulo, e não três colados.
 *
 * Colar retângulos de 1 módulo lado a lado deixa costura branca de
 * antialiasing entre eles em muita impressora — é a diferença entre um código
 * que bipa de primeira e um que exige três tentativas.
 *
 * `inicio` e `largura` estão em MÓDULOS. Quem desenha escolhe a escala.
 */
export function barrasEAN13(codigo: string): { inicio: number; largura: number }[] | null {
  const modulos = modulosEAN13(codigo)
  if (!modulos) return null

  const barras: { inicio: number; largura: number }[] = []
  let i = 0
  while (i < modulos.length) {
    if (modulos[i] === '0') {
      i++
      continue
    }
    const inicio = i
    while (i < modulos.length && modulos[i] === '1') i++
    barras.push({ inicio, largura: i - inicio })
  }
  return barras
}

/**
 * O número escrito embaixo do símbolo, no agrupamento da norma:
 * `7 891234 000124` — um dígito solto, depois dois grupos de seis.
 *
 * Devolve o código cru quando não é EAN-13: melhor mostrar o que está lá do
 * que espaçar errado um código que não é o que se pensava.
 */
export function legivelEAN13(codigo: string): string {
  if (!/^\d{13}$/.test(codigo)) return codigo
  return `${codigo[0]} ${codigo.slice(1, 7)} ${codigo.slice(7)}`
}
